import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AppShell from '../../components/AppShell';
import AdminTabs from '../../components/AdminTabs';
import { supabase } from '../../lib/supabase';
import type { ContentTable, Listing, Event as EventRow, BlogPost, Resource, Tip } from '@viet-parenting/supabase/types';

type PendingListing = Pick<Listing, 'id' | 'name_vi' | 'slug' | 'venue_city' | 'created_at'>;
type PendingEvent = Pick<EventRow, 'id' | 'name_vi' | 'slug' | 'venue_city' | 'starts_at'>;
type PendingPost = Pick<BlogPost, 'id' | 'title_vi' | 'slug'>;
type PendingResource = Pick<Resource, 'id' | 'name_vi' | 'slug' | 'country' | 'created_at'>;
type PendingTip = Pick<Tip, 'id' | 'tip' | 'submitter_name' | 'created_at'>;

interface QueueState {
  loading: boolean;
  listings: PendingListing[];
  events: PendingEvent[];
  posts: PendingPost[];
  resources: PendingResource[];
  tips: PendingTip[];
  error: string | null;
}

const EMPTY: QueueState = {
  loading: true,
  listings: [],
  events: [],
  posts: [],
  resources: [],
  tips: [],
  error: null,
};

function truncate(text: string, max = 80): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

export default function AdminQueue() {
  const [state, setState] = useState<QueueState>(EMPTY);

  useEffect(() => {
    let active = true;
    (async () => {
      const [l, e, p, r, ti] = await Promise.all([
        supabase
          .from('listings')
          .select('id, name_vi, slug, venue_city, created_at')
          .eq('status', 'pending')
          .order('created_at', { ascending: true }),
        supabase
          .from('events')
          .select('id, name_vi, slug, venue_city, starts_at')
          .eq('status', 'pending')
          .order('starts_at', { ascending: true }),
        supabase
          .from('blog_posts')
          .select('id, title_vi, slug')
          .eq('status', 'pending'),
        supabase
          .from('resources')
          .select('id, name_vi, slug, country, created_at')
          .eq('status', 'pending')
          .order('created_at', { ascending: true }),
        supabase
          .from('tips')
          .select('id, tip, submitter_name, created_at')
          .eq('status', 'pending')
          .order('created_at', { ascending: true }),
      ]);
      if (!active) return;
      const firstError = l.error ?? e.error ?? p.error ?? r.error ?? ti.error;
      setState({
        loading: false,
        listings: (l.data ?? []) as PendingListing[],
        events: (e.data ?? []) as PendingEvent[],
        posts: (p.data ?? []) as PendingPost[],
        resources: (r.data ?? []) as PendingResource[],
        tips: (ti.data ?? []) as PendingTip[],
        error: firstError ? firstError.message : null,
      });
    })();
    return () => {
      active = false;
    };
  }, []);

  const total =
    state.listings.length +
    state.events.length +
    state.posts.length +
    state.resources.length +
    state.tips.length;

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <AdminTabs active="queue" />
        <h1 className="font-display text-3xl font-extrabold text-red-700 mb-1">Hàng đợi duyệt</h1>
        <p className="text-sm text-gray-500 mb-8">
          {state.loading
            ? 'Đang tải…'
            : total === 0
              ? 'Không có nội dung nào đang chờ duyệt.'
              : `${total} nội dung đang chờ duyệt.`}
        </p>

        {state.error && (
          <div className="bg-red-50 text-red-700 text-sm rounded-xl px-5 py-4 mb-6 border border-red-100">
            {state.error}
          </div>
        )}

        {!state.loading && (
          <div className="space-y-8">
            <Group title="Lớp học" empty="Không có lớp học chờ duyệt." count={state.listings.length}>
              {state.listings.map((row) => (
                <Row
                  key={row.id}
                  type="listings"
                  id={row.id}
                  primary={row.name_vi}
                  secondary={row.venue_city || '—'}
                  date={row.created_at}
                />
              ))}
            </Group>
            <Group title="Sự kiện" empty="Không có sự kiện chờ duyệt." count={state.events.length}>
              {state.events.map((row) => (
                <Row
                  key={row.id}
                  type="events"
                  id={row.id}
                  primary={row.name_vi}
                  secondary={row.venue_city || '—'}
                  date={row.starts_at}
                />
              ))}
            </Group>
            <Group title="Bài viết" empty="Không có bài viết chờ duyệt." count={state.posts.length}>
              {state.posts.map((row) => (
                <Row key={row.id} type="blog_posts" id={row.id} primary={row.title_vi} />
              ))}
            </Group>
            <Group title="Tài nguyên" empty="Không có tài nguyên chờ duyệt." count={state.resources.length}>
              {state.resources.map((row) => (
                <Row
                  key={row.id}
                  type="resources"
                  id={row.id}
                  primary={row.name_vi}
                  secondary={row.country || '—'}
                  date={row.created_at}
                />
              ))}
            </Group>
            <Group title="Gợi ý" empty="Không có gợi ý mới." count={state.tips.length}>
              {state.tips.map((row) => (
                <li key={row.id}>
                  <Link
                    to={`/admin/tips/${row.id}`}
                    className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{truncate(row.tip)}</p>
                      {row.submitter_name && (
                        <p className="text-xs text-gray-500 truncate">{row.submitter_name}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-400 shrink-0">
                      <span>{new Date(row.created_at).toLocaleDateString('vi-VN')}</span>
                      <span>→</span>
                    </div>
                  </Link>
                </li>
              ))}
            </Group>
          </div>
        )}
      </div>
    </AppShell>
  );
}

interface GroupProps {
  title: string;
  empty: string;
  count: number;
  children: React.ReactNode;
}
function Group({ title, empty, count, children }: GroupProps) {
  return (
    <section>
      <h2 className="font-semibold text-gray-900 mb-3">
        {title} <span className="text-xs text-gray-400 font-normal">({count})</span>
      </h2>
      {count === 0 ? (
        <p className="text-sm text-gray-400">{empty}</p>
      ) : (
        <ul className="brand-card divide-y divide-[rgba(109,200,181,0.25)] overflow-hidden">{children}</ul>
      )}
    </section>
  );
}

const ROUTE: Record<ContentTable, string> = {
  listings: '/admin/listings',
  events: '/admin/events',
  blog_posts: '/admin/blog',
  resources: '/admin/resources',
};

interface RowProps {
  type: ContentTable;
  id: string;
  primary: string;
  secondary?: string;
  date?: string;
}
function Row({ type, id, primary, secondary, date }: RowProps) {
  const formattedDate = date ? new Date(date).toLocaleDateString('vi-VN') : null;
  return (
    <li>
      <Link
        to={`${ROUTE[type]}/${id}`}
        className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-gray-50 transition-colors"
      >
        <div className="min-w-0">
          <p className="font-medium text-gray-900 truncate">{primary}</p>
          {secondary && <p className="text-xs text-gray-500 truncate">{secondary}</p>}
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-400 shrink-0">
          {formattedDate && <span>{formattedDate}</span>}
          <span>→</span>
        </div>
      </Link>
    </li>
  );
}
